import React, { useId, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconSearch, IconMapMarkerAlt, IconArrowRight, IconHourglass } from '../ui/icons';
import { getProjects } from '../../data/projects';
import { formatXofCompact, formatRate } from '../../services/platformStats';
import { useProjects } from '../../context/ProjectContext';
import { MIN_INVESTMENT_LABEL } from '../../config/paymentMethods';
import ImageWithFallback from '../shared/ImageWithFallback';
import analytics from '../../utils/analytics';
import { collecteStatus } from '../../utils/collecte';

// Comparaison sans majuscules ni accents ("tillaberi" trouve "Tillabéri")
const normalize = (text) => String(text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const cityOf = (project) => String(project.location || '').split(',')[0].trim();

/** Valeurs distinctes d'un champ, avec leur nombre de projets, de la plus fréquente à la moins fréquente */
const countBy = (projects, keyOf) => {
  const counts = new Map();
  projects.forEach(project => {
    const key = keyOf(project);
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'fr'));
};

const FIELD = 'h-12 w-full rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200';

/** Carte d'un projet ouvert : photo, secteur, ville, avancement, montant restant, rendement visé */
const ProjectCard = ({ project }) => {
  const target = Number(project.coutTotal) || 0;
  const raised = Number(project.montantRecu) || 0;
  const percent = target > 0 ? Math.min(100, Math.round((raised / target) * 100)) : 0;
  const collecte = collecteStatus(project.finCollecte);
  return (
    <li className="flex snap-start flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <ImageWithFallback src={project.imageUrl} alt="" className="h-40 w-full object-cover" />
      <div className="flex flex-1 flex-col p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">{project.category}</p>
        <h3 className="mt-1 text-lg font-semibold text-gray-900">{project.titre}</h3>
        <p className="mt-1 flex items-center gap-1 text-sm text-gray-500">
          <IconMapMarkerAlt aria-hidden="true" /> {cityOf(project)}
        </p>
        <div className="mt-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Financé</span>
            <span className="font-semibold text-green-700">{percent} %</span>
          </div>
          <div className="mt-1 h-2 w-full rounded-full bg-gray-200" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}
            aria-label={`Financé à ${percent} %`}>
            <div className="h-2 rounded-full bg-green-600" style={{ width: `${percent}%` }} />
          </div>
          {collecte && (
            <p className={`mt-2 flex items-center gap-1 text-sm font-semibold ${collecte.ended ? 'text-gray-500' : 'text-orange-700'}`}>
              <IconHourglass aria-hidden="true" /> {collecte.label}
            </p>
          )}
        </div>
        <dl className="mb-5 mt-4 grid grid-cols-2 gap-2 text-sm">
          <div>
            <dt className="text-gray-500">Reste à financer</dt>
            <dd className="font-semibold text-gray-900">{formatXofCompact(Math.max(0, target - raised))}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Rendement visé</dt>
            <dd className="font-semibold text-gray-900">{Number(project.tauxRetour) > 0 ? `${formatRate(Number(project.tauxRetour) * 100)} / an` : '—'}</dd>
          </div>
        </dl>
        <Link to={`/projects/${project.id}`}
          className="mt-auto inline-flex min-h-[44px] items-center justify-center rounded-xl border-2 border-blue-700 px-4 py-2 font-semibold text-blue-800 hover:bg-blue-50">
          Voir le projet
        </Link>
      </div>
    </li>
  );
};

/**
 * 🔎 EXPLORATEUR DE PROJETS (accueil)
 * Recherche et filtres (secteur, ville) sur les projets ouverts, avec les vrais projets affichés
 * immédiatement. « Voir les projets » ouvre la page Projets avec les mêmes filtres ; la recherche
 * dans les articles reste disponible (page /search).
 */
const ProjectExplorer = () => {
  const id = useId();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [sector, setSector] = useState('');
  const [city, setCity] = useState('');
  // Projets avec l'avancement à jour (paiements confirmés), rechargés après chaque paiement par le contexte
  const { projects: liveProjects = [] } = useProjects() || {};

  const openProjects = useMemo(
    () => (liveProjects.length ? liveProjects : getProjects()).filter(p => {
      const target = Number(p.coutTotal) || 0;
      return !target || (Number(p.montantRecu) || 0) < target;
    }),
    [liveProjects]
  );
  const sectors = useMemo(() => countBy(openProjects, p => p.category), [openProjects]);
  const cities = useMemo(() => countBy(openProjects, cityOf), [openProjects]);

  const results = openProjects.filter(project => {
    const needle = normalize(query.trim());
    return (!sector || project.category === sector) &&
      (!city || cityOf(project) === city) &&
      (!needle || [project.titre, project.description, project.category, project.secteur, project.location]
        .some(field => normalize(field).includes(needle)));
  });

  const projectsLink = () => {
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    if (sector) params.set('secteur', sector);
    if (city) params.set('ville', city);
    const search = params.toString();
    return `/projects${search ? `?${search}` : ''}`;
  };

  const submit = (event) => {
    event.preventDefault();
    analytics.trackClick('home_project_explorer', { query: query.trim(), sector, city, results: results.length });
    navigate(projectsLink());
  };

  const label = results.length > 1 ? `Voir les ${results.length} projets` : results.length === 1 ? 'Voir le projet' : 'Voir tous les projets';

  return (
    <section className="bg-gray-50 py-16" aria-labelledby={`${id}-title`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-wider text-orange-600">Projets ouverts</p>
        <h2 id={`${id}-title`} className="mt-2 text-3xl font-bold text-gray-900">Trouvez un projet à financer</h2>
        <p className="mt-3 max-w-3xl text-lg text-gray-600">
          {openProjects.length} projet{openProjects.length > 1 ? 's' : ''} ouvert{openProjects.length > 1 ? 's' : ''} au Niger,
          dès {MIN_INVESTMENT_LABEL}, en financement ou en parts d'associé.
        </p>

        <form onSubmit={submit} role="search" aria-label="Rechercher un projet"
          className="mt-8 grid gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-[2fr_1fr_1fr_auto]">
          <div className="relative">
            <label htmlFor={`${id}-query`} className="sr-only">Mot-clé ou ville</label>
            <IconSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input id={`${id}-query`} type="search" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Mot-clé, ville, type de projet…" className={`${FIELD} pl-10`} />
          </div>
          <div>
            <label htmlFor={`${id}-sector`} className="sr-only">Secteur</label>
            <select id={`${id}-sector`} value={sector} onChange={e => setSector(e.target.value)} className={FIELD}>
              <option value="">Tous les secteurs</option>
              {sectors.map(([name, count]) => <option key={name} value={name}>{name} ({count})</option>)}
            </select>
          </div>
          <div>
            <label htmlFor={`${id}-city`} className="sr-only">Ville</label>
            <select id={`${id}-city`} value={city} onChange={e => setCity(e.target.value)} className={FIELD}>
              <option value="">Toutes les villes</option>
              {cities.map(([name, count]) => <option key={name} value={name}>{name} ({count})</option>)}
            </select>
          </div>
          <button type="submit"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 font-semibold text-white hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300">
            {label} <IconArrowRight aria-hidden="true" />
          </button>
        </form>

        {/* Secteurs représentés, avec leur nombre de projets ouverts (données réelles) */}
        <div className="mt-4 flex flex-wrap items-center gap-2" role="group" aria-label="Filtrer par secteur">
          {sectors.map(([name, count]) => (
            <button key={name} type="button" aria-pressed={sector === name}
              onClick={() => setSector(current => (current === name ? '' : name))}
              className={`min-h-[36px] rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                sector === name ? 'border-blue-700 bg-blue-700 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400'
              }`}>
              {name} ({count})
            </button>
          ))}
        </div>

        <p className="sr-only" aria-live="polite">{results.length} projet(s) correspondant(s)</p>

        {results.length > 0 ? (
          // Téléphone : cartes qui défilent horizontalement ; écran plus large : grille de 2 puis 4 colonnes
          <ul className="mt-8 grid snap-x grid-flow-col auto-cols-[80%] gap-4 overflow-x-auto pb-2 sm:grid-flow-row sm:auto-cols-auto sm:grid-cols-2 sm:overflow-visible lg:grid-cols-4">
            {results.slice(0, 4).map(project => <ProjectCard key={project.id} project={project} />)}
          </ul>
        ) : (
          <div className="mt-8 rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-gray-600">
            Aucun projet ouvert ne correspond à cette recherche.{' '}
            <button type="button" onClick={() => { setQuery(''); setSector(''); setCity(''); }} className="font-semibold text-blue-800 underline">
              Effacer les filtres
            </button>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          {query.trim() ? (
            <Link to={`/search?q=${encodeURIComponent(query.trim())}`} className="font-medium text-blue-800 hover:underline">
              Chercher « {query.trim()} » aussi dans les guides et actualités
            </Link>
          ) : <span />}
          <Link to="/projects" className="inline-flex items-center gap-2 font-semibold text-blue-800 hover:underline">
            Voir tous les projets <IconArrowRight aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProjectExplorer;
